import * as React from 'react';
import { useState, useCallback, useMemo } from 'react';

/** Material UI Packages. */
import { Box, Switch, Tooltip } from '@mui/material';
import { GridActionsCellItem, GridRowModes, useGridApiRef } from '@mui/x-data-grid-premium';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { addRateType, updateRateType, deleteRateType } from 'shared/store/slices/rateTypeSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { CustomSkeleton2 } from 'shared/ui/Skeleton';
import CustomSnackbar from 'shared/ui/CustomSnackbar';
import ThemeButton from 'shared/ui/Button';
import { MainDatagrid } from 'shared/ui/Datagrid';
import { createSocketData, updateSocketData, deleteSocketData } from 'shared/lib/fetchSocket';

/**
 * 담당자가 견적·요금에 쓸 요금 종류를 직접 늘리고 줄이는 화면.
 * 탭 번호는 화면이 받은 값을 그대로 쓴다 — 여기에 번호를 따로 적어 두면
 * 탭이 재배치될 때 조회와 쓰기가 서로 다른 탭 소관으로 갈라진다.
 */
export default function RateType({ loading, tabValue }) {
    const dispatch = useDispatch();
    const apiRef = useGridApiRef();
    const rateTypes = useSelector((state) => state.rateTypes);

    const [rowModesModel, setRowModesModel] = useState({});

    const isAnyRowEditing = useMemo(
        () => Object.values(rowModesModel).some((m) => m.mode === GridRowModes.Edit),
        [rowModesModel],
    );

    const handleNewClick = useCallback(() => {
        if (isAnyRowEditing) {
            dispatch(setSnackbar({ open: true, message: 'Please save the current row before adding a new one.', severity: 'warning' }));
            return;
        }
        const id = randomId();
        dispatch(addRateType({ id, name: '', active: true, _isNew: true }));
        setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'name' } }));
    }, [isAnyRowEditing, dispatch]);

    const handleEditClick = useCallback((id) => {
        setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit, fieldToFocus: 'name' } }));
    }, []);

    const handleSaveClick = useCallback((id) => {
        setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }));
    }, []);

    const handleCancelClick = useCallback((id) => {
        const row = rateTypes.find((r) => r.id == id);
        if (row?._isNew) {
            dispatch(deleteRateType(id));
            setRowModesModel((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            return;
        }
        setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View, ignoreModifications: true } }));
    }, [rateTypes, dispatch]);

    const handleDeleteClick = useCallback((id) => {
        if (!window.confirm('Are you sure you want to delete this rate type?')) return;
        deleteSocketData(dispatch, tabValue, 'row', 'il_rate_type', 'deleteRateType', id,
            (deletedId) => {
                dispatch(deleteRateType(deletedId ?? id));
                dispatch(setSnackbar({ open: true, message: 'Successfully deleted the rate type.', severity: 'success' }));
            },
        );
    }, [dispatch, tabValue]);

    const handleActiveToggle = useCallback((row) => {
        const updated = { ...row, active: !row.active };
        updateSocketData(dispatch, tabValue, 'row', 'il_rate_type', 'updateRateType', updated,
            (res) => dispatch(updateRateType(res.data ?? updated)),
        );
    }, [dispatch, tabValue]);

    const processRowUpdate = useCallback((newRow) => {
        const { _isNew, ...payload } = newRow;
        if (_isNew) {
            return new Promise((resolve, reject) => {
                if (!payload.name?.trim()) {
                    dispatch(setSnackbar({ open: true, message: 'Name is required.', severity: 'warning' }));
                    reject(new Error('Name is required.'));
                    return;
                }
                createSocketData(dispatch, tabValue, 'row', 'il_rate_type', 'addRateType', payload,
                    (res) => {
                        dispatch(deleteRateType(newRow.id));
                        dispatch(addRateType(res.data));
                        setRowModesModel((prev) => {
                            const next = { ...prev };
                            delete next[newRow.id];
                            return next;
                        });
                        resolve(res.data);
                    },
                    () => reject(new Error('Create failed.')),
                );
            });
        }
        return new Promise((resolve, reject) => {
            if (!payload.name?.trim()) {
                dispatch(setSnackbar({ open: true, message: 'Name is required.', severity: 'warning' }));
                reject(new Error('Name is required.'));
                return;
            }
            updateSocketData(dispatch, tabValue, 'row', 'il_rate_type', 'updateRateType', payload,
                (res) => {
                    dispatch(updateRateType(res.data ?? payload));
                    dispatch(setSnackbar({ open: true, message: 'Successfully updated the rate type.', severity: 'success' }));
                    resolve(res.data ?? payload);
                },
                () => reject(new Error('Update failed.')),
            );
        });
    }, [dispatch, setRowModesModel, tabValue]);

    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: 'Name',
            flex: 1,
            editable: true,
        },
        {
            field: 'active',
            headerName: 'Active',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Switch
                    checked={!!params.value}
                    size="small"
                    disabled={rowModesModel[params.id]?.mode === GridRowModes.Edit}
                    onChange={() => handleActiveToggle(params.row)}
                    sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#00647e' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#00647e' },
                    }}
                />
            ),
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            width: 100,
            getActions: ({ id, row }) => {
                if (rowModesModel[id]?.mode === GridRowModes.Edit) {
                    return [
                        <Tooltip title="Save" key="save">
                            <GridActionsCellItem icon={<SaveIcon />} label="Save" onClick={() => handleSaveClick(id)} color="inherit" />
                        </Tooltip>,
                        <Tooltip title="Cancel" key="cancel">
                            <GridActionsCellItem icon={<CancelIcon />} label="Cancel" onClick={() => handleCancelClick(id)} color="inherit" />
                        </Tooltip>,
                    ];
                }
                return [
                    <Tooltip title="Edit" key="edit">
                        <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={() => handleEditClick(id)} color="inherit" />
                    </Tooltip>,
                    <Tooltip title="Delete" key="delete">
                        <GridActionsCellItem icon={<DeleteIcon />} label="Delete" onClick={() => handleDeleteClick(id)} color="inherit" />
                    </Tooltip>,
                ];
            },
        },
    ], [rowModesModel, handleEditClick, handleSaveClick, handleCancelClick, handleDeleteClick, handleActiveToggle]);

    if (loading) return <CustomSkeleton2 />;

    return (
        <Box sx={{ my: 1, maxWidth: 800, mx: 'auto' }}>
            <CustomSnackbar />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <ThemeButton text="NEW" icon={<AddIcon />} width="72px" onClick={handleNewClick} />
            </Box>
            <MainDatagrid
                rows={rateTypes}
                cols={columns}
                showToolbar="GridToolbar"
                loading={false}
                id_status={false}
                additional={{
                    editMode: 'row',
                    rowModesModel,
                    handleRowModesModelChange: setRowModesModel,
                    processRowUpdate,
                    onProcessRowUpdateError: (err) => console.error(err),
                }}
            />
        </Box>
    );
}
